/// «Каса» in the panel (specs/cash-desk.md) — its own file, one line in
/// uk.ts, so that sections built in parallel merge cleanly.

/// ISO weekdays, Monday first — the order a club's week is read in.
const DAY_NAMES = ['Понеділок', 'Вівторок', 'Середа', 'Четвер', 'Пʼятниця', 'Субота', 'Неділя'] as const
const DAY_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'] as const

export const cashUk = {
  dayNames: DAY_NAMES,
  dayShort: DAY_SHORT,

  settings: {
    companyTab: 'Компанія',
    tab: 'Каса',
    subtitle: 'ФОП, на яких пишуться чеки, їхні дні в кожному клубі й банківські термінали.',
    readOnly: 'Змінювати ФОП, розклад і термінали може той, хто веде фінанси компанії.',
    club: 'Клуб',
  },

  payees: {
    title: 'ФОП',
    add: 'Додати ФОП',
    empty: 'Ще немає жодного ФОП',
    emptyMessage: 'Додайте ФОП, на яких працює клуб, — потім розставте їх по днях тижня.',
    footnote:
      'Чек пишеться на ФОП дня; касир може змінити його на зміну чи на один чек, і це видно в Z-звіті. Поки eatlete не фіскалізує сам, чеки кожного ФОП бʼють у його власному ПРРО — як і зараз.',
    inactive: 'Не працює',
    group: { '1': '1 група', '2': '2 група', '3': '3 група', other: 'Загальна система' } as Record<string, string>,
    groupShort: { '1': '1', '2': '2', '3': '3', other: 'Загальна' } as Record<string, string>,
    fiscal: { external: 'Власний ПРРО', none: 'Без ПРРО', checkbox: 'Checkbox' } as Record<string, string>,
    taxIdLine: (taxId: string) => `РНОКПП ${taxId}`,
    notScheduled: 'Ще не в розкладі',
  },

  payee: {
    newTitle: 'Новий ФОП',
    editTitle: 'ФОП',
    description: 'На цього ФОП пишуться чеки. РНОКПП і IBAN бачать лише ті, хто веде фінанси.',
    name: 'Назва для чека',
    namePlaceholder: 'ФОП Коваль Олена Вікторівна',
    nameHint: 'Так, як її друкує чек.',
    taxId: 'РНОКПП',
    taxIdPlaceholder: '10 цифр',
    taxIdHint: 'Реєстраційний номер облікової картки платника податків.',
    iban: 'IBAN',
    ibanPlaceholder: 'UA00 0000 0000 0000 0000 0000 0000 0',
    ibanHint: 'Рахунок ФОП — сюди приходять гроші з його терміналу. Можна додати пізніше.',
    taxGroup: 'Група єдиного податку',
    fiscalMode: 'Фіскальні чеки',
    fiscalChoices: {
      external: { title: 'Власний ПРРО клубу', subtitle: 'Чеки — у вашому застосунку ПРРО, як зараз' },
      none: { title: 'Без ПРРО', subtitle: 'Лише для 1 групи єдиного податку' },
    },
    checkboxLater: 'Чеки через Checkbox прямо з eatlete зʼявляться згодом.',
    active: 'Працює',
    activeHint: 'ФОП, що не працює, не стоїть у розкладі й не бере нових чеків. Його чеки лишаються.',
    create: 'Додати ФОП',
    creating: 'Додаємо…',
    created: (name: string) => `${name} — додано`,
    saved: (name: string) => `${name} — збережено`,
    retired: (name: string) => `${name} більше не працює`,
    restored: (name: string) => `${name} знову працює`,
  },

  week: {
    title: 'Розклад по днях',
    today: 'сьогодні',
    nobody: 'Нікого',
    footnote:
      'Зміна відкривається на ФОП свого дня й тримає його до закриття — навіть після півночі. День — за годинником клубу.',
    saving: 'Зберігаємо…',
    saved: 'Розклад збережено',
    needsPayees: 'Спершу додайте ФОП — тоді їх можна буде розставити по днях.',
    dayLabel: (day: string) => `${day}: ФОП дня`,
  },

  terminals: {
    title: 'Термінали',
    add: 'Додати термінал',
    empty: 'У цьому клубі ще немає терміналу',
    footnote:
      'Картку під ФОП приймає лише його термінал — інакше банк зарахує гроші не тому, хто в чеку. ФОП без терміналу в клубі приймає лише готівку.',
    inactive: 'Вимкнено',
    newTitle: 'Новий термінал',
    editTitle: 'Термінал',
    description: 'Банківський термінал у клубі. eatlete його не керує — лише записує, через який термінал пройшла картка.',
    name: 'Назва',
    namePlaceholder: 'Термінал ПриватБанк',
    payee: 'ФОП',
    payeeHint: 'Чий рахунок отримує гроші з цього терміналу.',
    payeePlaceholder: 'Оберіть ФОП',
    club: 'Клуб',
    clubFixed: 'Клуб термінала не змінюється: перенесений термінал додайте в новому клубі.',
    active: 'Працює',
    create: 'Додати термінал',
    created: (name: string) => `${name} — додано`,
    saved: (name: string) => `${name} — збережено`,
  },

  nav: 'Каса',

  desk: {
    title: 'Каса',
    subtitle: 'Зміна з перерахунком: що в касі на початку, що зайшло й вийшло, і скільки має бути зараз.',
    tabs: { shift: 'Зміна', history: 'Історія змін' },
    club: 'Клуб',
    noClubs: 'У вас немає клубу з касою',
    readOnly: 'Ви бачите касу, але відкривати й закривати зміни може адміністратор.',
  },

  shift: {
    closedTitle: 'Зміна закрита',
    closedMessage: (left: string) => `У касі має бути ${left} — залишок попередньої зміни. Перерахуйте й відкрийте нову.`,
    firstMessage: 'Це перша зміна цієї каси. Перерахуйте гроші в шухляді й відкрийте її.',
    open: 'Відкрити зміну',
    title: (number: number) => `Зміна ${number}`,
    openedBy: (club: string, time: string, who: string) => `${club} · відкрита о ${time} · ${who}`,
    expected: 'Має бути в касі',
    expectedHint: 'Початок зміни плюс внесення, мінус інкасації. Чеки додадуться, коли каса почне продавати.',
    payee: 'ФОП зміни',
    payeeSince: (time: string) => `з ${time}`,
    switchPayee: 'Змінити ФОП',
    cashIn: 'Внесення',
    collection: 'Інкасація',
    xReport: 'X-звіт',
    close: 'Закрити зміну',
    movements: 'Рух готівки',
    noMovements: 'За зміну готівка ще не рухалась',
    opening: 'Початок зміни',
    openingDiscrepancy: (amount: string) => `Розбіжність на відкритті: ${amount}`,
    switches: 'Зміна ФОП',
    switchLine: (from: string, to: string) => `${from} → ${to}`,
  },

  openPanel: {
    title: 'Відкрити зміну',
    description: 'Перерахуйте гроші в касі. Якщо сума не та, що лишила попередня зміна, напишіть чому — розбіжність приймає старший адміністратор.',
    expected: 'Має бути',
    expectedFirst: 'Перша зміна каси — нічого не очікується',
    counted: 'Пораховано',
    countedPlaceholder: '0',
    payee: 'ФОП зміни',
    payeeHint: (day: string) => `ФОП дня за розкладом клубу (${day.toLowerCase()}). Можна обрати іншого — це запишеться.`,
    payeeOther: (name: string) => `За розкладом сьогодні ${name}. Зміна запише, що обрано іншого.`,
    note: 'Чому сума інша',
    notePlaceholder: 'Наприклад: розмінний фонд від власника',
    submit: 'Відкрити зміну',
    submitting: 'Відкриваємо…',
    opened: (number: number) => `Зміну ${number} відкрито`,
  },

  movement: {
    cashInTitle: 'Внесення',
    cashInDescription: 'Гроші, що зайшли в касу не з продажу: розмін, повернення із сейфа.',
    collectionTitle: 'Інкасація',
    collectionDescription: 'Гроші, що вийшли з каси не на повернення: у сейф, у банк, власнику.',
    amount: 'Сума',
    reason: 'Причина',
    reasonPlaceholderIn: 'Розмін із сейфа',
    reasonPlaceholderOut: 'Інкасація в сейф',
    collectTo: 'Куди',
    collectToOptions: { safe: 'Сейф', bank: 'Банк', owner: 'Власнику' } as Record<string, string>,
    submit: 'Записати',
    submitting: 'Записуємо…',
    written: (kind: string, amount: string) => `${kind}: ${amount}`,
    atClose: 'Інкасація при закритті',
    correction: 'Виправлення',
  },

  switchPanel: {
    title: 'Змінити ФОП',
    description: 'Чеки до кінця зміни підуть на іншого ФОП. Зміну видно в Z-звіті: хто, коли й чому.',
    payee: 'Новий ФОП',
    choose: 'Оберіть ФОП',
    reason: 'Причина',
    reasonPlaceholder: 'За вказівкою власника',
    submit: 'Змінити ФОП',
    switched: (name: string) => `Далі чеки — на ${name}`,
  },

  x: {
    title: (number: number) => `X-звіт · зміна ${number}`,
    description: (time: string) => `Підсумки зміни станом на ${time}. Звіт нічого не змінює.`,
    opening: 'Початок зміни',
    cashIn: 'Внесення',
    collections: 'Інкасації',
    correctionsIn: 'Виправлення +',
    correctionsOut: 'Виправлення −',
    sales: 'Продажі готівкою',
    salesLater: 'зʼявляться з чеками',
    expected: 'Має бути в касі',
    payees: 'ФОП зміни',
    fromOpening: 'З відкриття зміни',
  },

  closePanel: {
    title: (number: number) => `Закрити зміну ${number}`,
    description: 'Перерахуйте гроші в касі. Закриту зміну не відкрити знову — помилку виправляють у наступній.',
    expected: 'Має бути',
    counted: 'Пораховано',
    matches: 'Сходиться',
    shortage: (amount: string) => `Нестача ${amount}`,
    surplus: (amount: string) => `Надлишок ${amount}`,
    leave: 'Залишити в касі',
    leaveHint: 'Розмінний фонд для наступної зміни.',
    collectTo: 'Решту — в',
    collect: (amount: string) => `Інкасація при закритті: ${amount}`,
    note: 'Чому не сходиться',
    notePlaceholder: 'Наприклад: помилилась зі здачею',
    needsManager: 'Розбіжність приймає старший адміністратор або керуючий. Попросіть підтвердження — вони побачать суму й вашу примітку у своїй «Касі».',
    ask: 'Попросити підтвердження',
    asking: 'Просимо…',
    waiting: 'Чекаємо на підтвердження…',
    approved: (who: string) => `Розбіжність підтверджено · ${who}`,
    rejected: (who: string) => `Розбіжність не підтверджено · ${who}. Перерахуйте ще раз`,
    expired: 'Підтвердження вже не діє — попросіть знову',
    submit: 'Закрити зміну',
    submitting: 'Закриваємо…',
    closed: (number: number) => `Зміну ${number} закрито`,
  },

  approvals: {
    title: 'Чекає на ваше підтвердження',
    line: (who: string, what: string, when: string) => `${who}: ${what} ${when}`,
    open: 'на відкритті зміни',
    close: 'на закритті зміни',
    shortage: (amount: string) => `нестача ${amount}`,
    surplus: (amount: string) => `надлишок ${amount}`,
    approve: 'Підтвердити',
    reject: 'Відхилити',
    approved: 'Підтверджено',
    rejected: 'Відхилено',
  },

  history: {
    title: 'Історія змін',
    empty: 'Ще не було жодної зміни',
    emptyMessage: 'Закриті зміни зʼявляться тут разом із Z-звітом.',
    open: 'Відкрита',
    span: (from: string, to: string) => `${from} – ${to}`,
    expected: 'Має бути',
    counted: 'Пораховано',
    z: (number: number) => `Z-звіт · зміна ${number}`,
    zDescription: 'Знімок на момент закриття. Змінити його не можна.',
    closedBy: 'Закриття',
    openedBy: 'Відкриття',
    left: 'Залишено в касі',
    collected: 'Інкасація при закритті',
    note: 'Примітка',
    difference: 'Розбіжність',
    approvedBy: 'Підтвердження розбіжності',
  },

  errors: {
    nameRequired: 'Вкажіть назву',
    taxIdShape: 'РНОКПП — це 10 цифр',
    invalidTaxId: 'РНОКПП не проходить перевірку — схоже, одна з цифр не та',
    ibanShape: 'IBAN українського рахунку — це UA і ще 27 цифр',
    invalidIban: 'IBAN не проходить перевірку — схоже, одна з цифр не та',
    taxIdTaken: 'ФОП з таким РНОКПП уже є в компанії',
    fiscalNotAllowed: 'Без ПРРО може працювати лише ФОП 1 групи',
    fiscalNotAvailable: 'Checkbox через eatlete ще не підключено — оберіть власний ПРРО',
    payeeScheduled: (where: string) => `Цей ФОП ще стоїть у розкладі: ${where}. Спершу приберіть його з цих днів.`,
    payeeInactive: 'Цей ФОП не працює — оберіть іншого або знову увімкніть його',
    unknownPayee: 'Такого ФОП у компанії немає — оновіть сторінку',
    unknownTerminal: 'Такого терміналу немає — оновіть сторінку',
    unknownBranch: 'Такого клубу немає або він не ваш',
    forbidden: 'ФОП, розклад і термінали змінює той, хто керує фінансами компанії',
    companyScope: 'ФОП змінює лише той, хто працює на всю компанію, а не на окремі клуби',
    outOfScope: 'Це не ваш клуб — його розклад і термінали змінює керівник',
    keyReused: 'Цю форму вже надіслано з іншими даними — закрийте її й почніть знову',
    terminalNameRequired: 'Назвіть термінал',
    payeeRequired: 'Оберіть ФОП',
    amountRequired: 'Вкажіть суму в гривнях',
    reasonRequired: 'Напишіть причину',
    noteRequired: 'Напишіть, чому сума не сходиться',
    leaveTooMuch: 'Залишити можна не більше, ніж пораховано',
    dayWithoutPayee: 'На сьогодні в розкладі немає ФОП — оберіть, на кого відкрити зміну',
    shiftAlreadyOpen: 'На цій касі вже відкрита зміна — оновіть сторінку',
    shiftNotOpen: 'Зміну вже закрито — оновіть сторінку',
    insufficientCash: (expected: string) => `У касі має бути лише ${expected} — більше не вийде`,
    discrepancyNeedsManager: 'Розбіжність приймає старший адміністратор — попросіть підтвердження',
    approvalMismatch: 'Сума змінилася після підтвердження — попросіть ще раз',
    approvalPending: 'Підтвердження ще не надійшло',
    approvalRejected: 'Підтвердження відхилено — перерахуйте ще раз',
    approvalSpent: 'Це підтвердження вже використано',
    approvalExpired: 'Підтвердження вже не діє — попросіть знову',
    approvalSelf: 'Свою розбіжність підтверджує хтось інший',
    approvalDecided: 'Хтось уже вирішив',
    approvalNotNeeded: 'Сума сходиться — підтвердження не потрібне',
    payeeUnchanged: 'Зміна вже на цьому ФОП',
    payeeInOpenShift: 'На цьому ФОП зараз працює відкрита зміна — спершу змініть ФОП зміни',
    unknownShift: 'Такої зміни немає — оновіть сторінку',
    unknownTill: 'Такої каси немає — оновіть сторінку',
  },
}
